import React from 'react';
import {useTranslation} from 'react-i18next';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  ModalSubmitButton,
  showModal,
  showNewNotification,
} from '@edna/components';

import {Text} from 'src/components/primitives';
import {ClockImage} from 'src/images';

import {useCancelConnectedTariffChangingMutation, useDeleteAvailableTariffMutation} from '../api';
import {TAvailableTariff} from '../definitions';
import * as S from './style';

const Content = React.memo<{
  closeModal: TEmptyFunction;
  handleSubmit: TEmptyFunction;
  isLoading?: boolean;
}>(({closeModal, handleSubmit, isLoading}) => {
  const {t} = useTranslation();

  return (
    <Modal width="430px">
      <ModalHeader>{t('Tariffs:cancelChangesModal.title')}</ModalHeader>
      <ModalContent>
        <Text secondary>{t('Tariffs:cancelChangesModal.subtitle')}</Text>
      </ModalContent>
      <S.CancelChangesImage as={ClockImage} />
      <ModalActions>
        <ModalCancelButton onClick={closeModal}>{t('Common:button.back')}</ModalCancelButton>
        <ModalSubmitButton onClick={handleSubmit} disabled={isLoading} loading={isLoading}>
          {t('Tariffs:cancelChangesModal.submitButton')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

Content.displayName = 'Content';

const CancelTariffChangesModal = React.memo<{closeModal: TEmptyFunction; tariff: TAvailableTariff}>(
  ({closeModal, tariff}) => {
    const {t} = useTranslation();

    const [deleteTariff, {isLoading}] = useDeleteAvailableTariffMutation();

    const handleSubmit = React.useCallback(async () => {
      try {
        await deleteTariff({id: tariff.id, version: tariff.version}).unwrap();
        closeModal();
        showNewNotification({
          type: 'SUCCESS',
          message: t('Tariffs:successNotifications.cancelChanges', {name: tariff.name}),
        });
      } catch (e) {}
    }, [tariff, closeModal]);

    return <Content handleSubmit={handleSubmit} isLoading={isLoading} closeModal={closeModal} />;
  },
);

CancelTariffChangesModal.displayName = 'CancelTariffChangesModal';

const CancelConnectedTariffChangesModal = React.memo<{
  closeModal: TEmptyFunction;
  id: number;
  version: number;
  name: string;
}>(({closeModal, id, version, name}) => {
  const {t} = useTranslation();

  const [cancelChanges, {isLoading}] = useCancelConnectedTariffChangingMutation();

  const handleSubmit = async () => {
    try {
      await cancelChanges({id, version}).unwrap();
      closeModal();
      showNewNotification({
        type: 'SUCCESS',
        message: t('Tariffs:successNotifications.cancelChanges', {name}),
      });
    } catch (e) {}
  };

  return <Content handleSubmit={handleSubmit} isLoading={isLoading} closeModal={closeModal} />;
});

CancelConnectedTariffChangesModal.displayName = 'CancelConnectedTariffChangesModal';

export const showCancelConnectedTariffChangesModal = ({
  id,
  version,
  name,
}: {
  id: number;
  version: number;
  name: string;
}) =>
  showModal((closeModal) => (
    <CancelConnectedTariffChangesModal
      closeModal={closeModal}
      id={id}
      version={version}
      name={name}
    />
  ));

export const showCancelTariffChangesModal = (tariff: TAvailableTariff) =>
  showModal((closeModal) => <CancelTariffChangesModal closeModal={closeModal} tariff={tariff} />);
